// La spirale du découvert : rien n'empêche de tomber, mais la chute a des marches, comme dans la vraie vie.
// Au-delà d'une tolérance (qui suit le revenu), la banque rejette les prélèvements avec des frais ; après
// trois mois de dépassement, elle convoque et propose de transformer le découvert en crédit, sinon elle
// retire la carte (mise en demeure) ; 60 jours plus tard, 500 € encore dépassés valent un fichage FICP
// (plus aucun crédit) ; et quand les dettes écrasent les revenus, le dossier de surendettement gèle tout,
// impose un plan, et finit par effacer ce qui ne peut pas être remboursé. L'école privée, elle, n'est pas
// payée quand la banque rejette : impayée deux fois, elle exclut, et on cherche un métier au niveau atteint.
import { PATHS } from '../content/paths';
import { clamp, loanMonthlyPayment, round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import type { GameState } from '../engine/state';
import {
  addJournal,
  addLoan,
  addNotice,
  overdraftLimit,
  payFromCash,
  scheduleFollowUp,
} from './actions';
import { isLoanActive, monthlyIncome, withdrawFromLdds, withdrawFromLep, withdrawFromLivret } from './economy';
import { regulatedSavings } from './livret';

export const DECOUVERT = {
  /** La tolérance de la banque avant les rejets : la moitié d'un mois de revenus, entre 500 et 1 000 €. */
  toleranceShare: 0.5,
  toleranceMin: 500,
  toleranceMax: 1000,
  /** Les frais d'incidents du mois, plafonnés comme pour un client fragile. */
  rejectFees: 25,
  callFees: 10,
  regularizeFees: 20,
  /** Trois mois d'affilée au-delà de l'autorisé : la banque doit proposer un crédit (la vraie règle). */
  convocationMonths: 3,
  restructureRate: 0.08,
  restructureMonths: 24,
  /** La mise en demeure : 60 jours pour revenir sous 500 € de dépassement, sinon le fichage. */
  ficpDays: 60,
  ficpFloor: 500,
  /** Le dossier de surendettement : des dettes valant plus de six mois de revenus, sans épargne. */
  debtMonths: 6,
  planMonths: 84,
  /** Le reste à vivre du plan : ce que la banque laisse sur le compte chaque mois. */
  planKeep: 300,
  /** Sans carte (retirée, ou pendant le plan), tout achat coûte un peu plus. */
  purchasePenalty: 1.05,
  /** Le délai accordé par l'école avant le dernier rappel. */
  schoolDelayDays: 90,
  /** Exclu de l'école : deux mois de recherche, pour un métier au niveau d'études déjà validé. */
  searchDays: 60,
  salaryPerYear: 130,
};

/** La tolérance de la banque avant de rejeter : suit le revenu, avec un petit aléa propre à la partie. */
export function rejectionThreshold(state: GameState): number {
  const base = monthlyIncome(state) * DECOUVERT.toleranceShare + state.overdraftJitter;
  return clamp(Math.round(base / 10) * 10, DECOUVERT.toleranceMin, DECOUVERT.toleranceMax);
}

/** Le dépassement : ce qui est dû au-delà du découvert autorisé. */
export function depassement(state: GameState): number {
  return round2(Math.max(0, -state.cash - overdraftLimit(state)));
}

/** Sans carte bancaire (retirée, ou pendant un plan de surendettement), les achats coûtent 5 % de plus. */
export function purchasePenalty(state: GameState): number {
  return state.cardWithdrawn || state.surendettement ? DECOUVERT.purchasePenalty : 1;
}

/** Les dettes exigibles : le dépassement, plus les crédits dont les mensualités courent. */
export function dettesExigibles(state: GameState): number {
  // Le crédit immobilier n'en fait pas partie : il est adossé au logement.
  const loans = state.loans
    .filter((loan) => isLoanActive(state, loan) && !loan.mortgage)
    .reduce((sum, loan) => sum + loan.balance, 0);
  return round2(depassement(state) + loans);
}

/** Programme une carte de la spirale (via les suites), sans doublon ni retour trop rapproché. */
function queue(state: GameState, id: string, params: Record<string, number>, minGapDays: number): void {
  if (state.followUps.some((followUp) => followUp.id === id)) return;
  const last = state.eventLastDays[id];
  if (last !== undefined && state.day - last < minGapDays) return;
  scheduleFollowUp(state, id, 1, params);
}

/** Remonte le compte dans le découvert autorisé avec les livrets, frais de régularisation compris. */
export function coverOverdraftFromSavings(state: GameState): void {
  const need = () => Math.max(0, -state.cash - overdraftLimit(state) + DECOUVERT.regularizeFees);
  if (need() > 0) withdrawFromLivret(state, Math.min(state.livret, Math.ceil(need())));
  if (need() > 0) withdrawFromLdds(state, Math.min(state.ldds, Math.ceil(need())));
  if (need() > 0) withdrawFromLep(state, Math.min(state.lep, Math.ceil(need())));
  payFromCash(state, DECOUVERT.regularizeFees);
}

/** La convocation acceptée : le dépassement devient un crédit classique, le compte revient dans l'autorisé. */
export function restructureOverdraft(state: GameState): { payment: number; interest: number } {
  const amount = depassement(state);
  const loan = addLoan(state, 'loan.decouvert', amount, DECOUVERT.restructureRate, DECOUVERT.restructureMonths);
  state.cash = round2(state.cash + amount);
  state.depassementMonths = 0;
  return { payment: loan.monthlyPayment, interest: loan.totalInterest };
}

/** La convocation refusée : la carte est retirée, la mise en demeure court. */
export function refuseConvocation(state: GameState): void {
  state.cardWithdrawn = true;
  state.miseEnDemeureDay = state.day;
  addJournal(state, 'banque.carteRetiree');
}

/** La mensualité qu'aurait le découvert transformé en crédit, pour l'annoncer sur la carte. */
export function restructurePayment(state: GameState): number {
  return round2(loanMonthlyPayment(depassement(state), DECOUVERT.restructureRate, DECOUVERT.restructureMonths));
}

/** Le dossier déposé : intérêts gelés, carte rendue au guichet, plan de sept ans au plus. */
export function startSurendettement(state: GameState): void {
  state.surendettement = { monthsLeft: DECOUVERT.planMonths };
  state.ficp = true;
  state.cardWithdrawn = true;
  state.miseEnDemeureDay = null;
  addJournal(state, 'plan.depose');
}

/** Tout est réglé : la banque tourne la page. */
function backToNormal(state: GameState, viaPlan: boolean): void {
  state.depassementMonths = 0;
  state.miseEnDemeureDay = null;
  if (state.ficp) {
    state.ficp = false;
    addNotice(state, 'banque.ficpLeve');
    addJournal(state, 'banque.ficpLeve');
  }
  if (state.cardWithdrawn) {
    state.cardWithdrawn = false;
    if (!viaPlan) {
      addNotice(state, 'banque.carteRendue');
      addJournal(state, 'banque.carteRendue');
    }
  }
}

/** Un mois de plan : la banque laisse un reste à vivre et prélève le reste vers les crédits. */
function planMonth(state: GameState): void {
  const plan = state.surendettement;
  if (!plan) return;
  let surplus = round2(Math.max(0, state.cash - DECOUVERT.planKeep));
  for (const loan of state.loans) {
    if (surplus <= 0) break;
    // Le crédit de la résidence principale garde ses mensualités, hors du plan.
    if (loan.mortgage) continue;
    const paid = Math.min(surplus, loan.balance);
    loan.balance = round2(loan.balance - paid);
    surplus = round2(surplus - paid);
    state.cash = round2(state.cash - paid);
    if (loan.balance === 0) addJournal(state, 'journal.loanPaid', { label: loan.label });
  }
  state.loans = state.loans.filter((loan) => loan.mortgage || loan.balance > 0);
  plan.monthsLeft -= 1;
  if (state.loans.every((loan) => loan.mortgage) && depassement(state) <= 0) {
    state.surendettement = null;
    backToNormal(state, true);
    addNotice(state, 'plan.sorti');
    addJournal(state, 'plan.sorti');
    return;
  }
  if (plan.monthsLeft <= 0) {
    // Le rétablissement personnel : ce qui ne peut pas être remboursé est effacé.
    const wiped = round2(
      state.loans.filter((loan) => !loan.mortgage).reduce((sum, loan) => sum + loan.balance, 0) + depassement(state),
    );
    state.loans = state.loans.filter((loan) => loan.mortgage);
    state.cash = Math.max(state.cash, -overdraftLimit(state));
    state.surendettement = null;
    backToNormal(state, true);
    const params = { amount: fmtEur(wiped) };
    addNotice(state, 'plan.efface', params);
    addJournal(state, 'plan.efface', params);
  }
}

/** Chaque fin de mois : les marches de la spirale, dans l'ordre où la vraie vie les descend. */
export function spiralMonth(state: GameState): void {
  if (state.surendettement) {
    planMonth(state);
    return;
  }
  const dep = depassement(state);
  if (dep <= 0) {
    backToNormal(state, false);
    return;
  }
  state.depassementMonths += 1;
  const threshold = rejectionThreshold(state);
  if (dep > threshold) {
    if (!state.flags.premierRejet) {
      state.flags = { ...state.flags, premierRejet: true };
      queue(state, 'premier_rejet', {}, 0);
    } else {
      queue(state, 'prelevements_rejetes', {}, 25);
    }
  }
  if (state.depassementMonths >= DECOUVERT.convocationMonths && !state.cardWithdrawn) {
    queue(state, 'banque_convocation', {}, 45);
  }
  if (
    state.miseEnDemeureDay !== null &&
    state.day - state.miseEnDemeureDay >= DECOUVERT.ficpDays &&
    dep >= DECOUVERT.ficpFloor &&
    !state.ficp
  ) {
    state.ficp = true;
    addNotice(state, 'banque.ficp');
    addJournal(state, 'banque.ficp');
  }
  if (
    dep > threshold &&
    regulatedSavings(state) < 100 &&
    dettesExigibles(state) > DECOUVERT.debtMonths * Math.max(500, monthlyIncome(state))
  ) {
    queue(state, 'dossier_surendettement', {}, 60);
  }
}

/** L'école n'a pas été payée : la banque a rejeté le prélèvement, l'école relance. */
export function schoolFeeRejected(state: GameState, fee: number): void {
  state.flags = { ...state.flags, ecoleImpayee: true };
  const params = { amount: fmtEur(fee) };
  addNotice(state, 'school.rejected', params);
  addJournal(state, 'school.rejected', params);
  scheduleFollowUp(state, 'ecole_impayee', 1, { fees: fee });
}

/** Les années d'études déjà validées (au plus quatre : la cinquième, c'est le diplôme). */
export function schoolYearsCompleted(state: GameState): number {
  if (state.firstJobTargetDay === null) return 0;
  const start = state.firstJobTargetDay - 5 * 365;
  return clamp(Math.floor((state.day - start) / 365), 0, 4);
}

/** L'exclusion : plus d'école à payer, deux mois de recherche, un métier au niveau d'études atteint. */
export function excludeFromSchool(state: GameState): number {
  const years = schoolYearsCompleted(state);
  const base = PATHS.work.salary + DECOUVERT.salaryPerYear * years;
  state.flags = { ...state.flags, excluEcole: true, ecoleImpayee: false };
  state.jobSalaryBase = base;
  state.firstJobTargetDay = state.day + DECOUVERT.searchDays;
  addJournal(state, 'journal.exclusion', { salary: fmtEur(base) });
  return base;
}
